export const heroContent = {
  eyebrow: 'Automated Statement Processing',
  title: 'Turn PDF Bank Statements into Clean, Structured Data.',
  description: 'BankStatementReader extracts transactions from PDF bank statements using OCR, then exports the results to Excel, CSV, or JSON — no manual retyping.',
  cta: {
    primary: { text: 'Start Free', to: '/sign-up' },
    secondary: { text: 'View Pricing', href: '#pricing' }
  },
  dataCards: [
    { title: 'Input', value: 'PDF', description: 'Digital or scanned statements' },
    { title: 'Exports', value: 'XLSX · CSV · JSON', description: 'Structured transaction tables' },
    { title: 'OCR Engine', value: 'PaddleOCR', description: 'Table-aware text extraction' },
    { title: 'Free Tier', value: '5 / mo', description: 'Statements per month, no card' }
  ]
}

export const aboutContent = {
  title: 'Take the Manual Lift out of Statement Reconciliation',
  description: 'BankStatementReader replaces hours of manual retyping with automated parsing and structured exports, so your finance ops can focus on review instead of data entry.'
}

export const problemsContent = {
  sectionTitle: "What's Slowing Down Your Close Today",
  sectionDescription: 'Finance teams still rely on spreadsheets, shared inboxes, and manual copying to reconcile cash activity. These friction points create delays, introduce risk, and make audits painful.',
  cards: [
    {
      title: 'Fragmented Formats',
      description: 'Scanned PDFs, image exports, and multi-language statements land differently every time, forcing analysts to rebuild templates from scratch.'
    },
    {
      title: 'Hidden Exceptions',
      description: 'Manual spot checks miss duplicate charges, reversals, and suspicious activity that demand real-time alerting.'
    },
    {
      title: 'Compliance Pressure',
      description: 'AUD, PCI, and FINTRAC require clean evidence trails. Manually stitching documents creates audit gaps and stress.'
    }
  ]
}

export const featuresContent = {
  sectionTitle: 'All the Insight You Need, Already Structured',
  sectionDescription: 'Live dashboards, intelligent enrichment, and configurable exports let finance teams monitor cash positions, reconcile faster, and satisfy auditors in minutes.',
  cards: [
    {
      title: 'Unified Statement Workspace',
      description: 'Drag-and-drop or email any statement format. Our normalization engine converts them into a consistent schema with fields for customer, instrument, counterparty, and GL mapping.',
      accent: true
    },
    {
      title: 'Adaptive Categorization',
      description: 'Machine learning models learn from your chart of accounts and auto-tag recurring vendors, subscription debits, and repayment schedules.'
    },
    {
      title: 'Exception Intelligence',
      description: 'Trigger alerts on duplicate withdrawals, threshold breaches, or missing payouts. Review flagged activity with a single click.'
    },
    {
      title: 'Audit-Ready Exports',
      description: 'Generate SOC 2 aligned logs with immutable hashes, or sync reconciled data back to NetSuite, ERPNext, and custom ERPs through secure APIs.'
    }
  ]
}

export const pricingContent = {
  sectionTitle: 'Simple, Transparent Pricing',
  sectionDescription: 'Choose the plan that meets your statement volume. Every tier includes SOC 2 compliant infrastructure, dedicated support, and API access.',
  tiers: [
    {
      name: 'Starter',
      price: '$79',
      period: '/month',
      description: 'Perfect for lean finance teams digitizing their first workflows.',
      features: [
        'Up to 500 statements / month',
        'Core extraction & normalization',
        'Webhook & CSV exports',
        'Email & chat support'
      ],
      cta: { text: 'Start Free Trial', to: '/sign-up' }
    },
    {
      name: 'Growth',
      price: '$199',
      period: '/month',
      description: 'Advanced automation for multi-entity controllers and revenue ops.',
      features: [
        'Up to 2,000 statements / month',
        'Advanced anomaly detection',
        'NetSuite, ERPNext, Odoo sync',
        'Role-based approvals & audit trail'
      ],
      cta: { text: 'Start Free Trial', to: '/sign-up' },
      highlight: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: ' pricing',
      description: 'Tailored onboarding, compliance automation, and dedicated SLAs.',
      features: [
        'Unlimited statements & data retention',
        'Private cloud or on-prem deployment',
        'Custom GL mapping & data lake exports',
        'Dedicated technical account manager'
      ],
      cta: { text: 'Talk to Sales', href: 'mailto:hello@bankstatementreader.com' }
    }
  ]
}

export const faqContent = {
  eyebrow: 'Everything You Need to Know',
  title: 'Frequently Asked Questions',
  description: 'From implementation timelines to security posture, here are the most common questions finance and compliance teams ask before rolling out BankStatementReader.',
  questions: [
    {
      question: 'How quickly can we deploy BankStatementReader?',
      answer: 'Most teams connect their storage and finance tools within 10 minutes. Our onboarding engineers help map your chart of accounts and approval rules in the first week.'
    },
    {
      question: 'Can you read image-only or scanned PDFs?',
      answer: 'Yes. Our OCR pipeline is trained on multi-language banking statements and automatically enhances low-resolution scans for accurate extraction.'
    },
    {
      question: 'Where does my data live?',
      answer: 'Data stays in-region (US, EU, or APAC), encrypted at rest (AES-256) and in transit (TLS 1.3). Optional on-premise deployments are available for regulated industries.'
    },
    {
      question: 'Do you offer human-in-the-loop review?',
      answer: 'Growth and Enterprise plans include access to certified financial analysts who can validate anomalies and sign off on large reconciliations.'
    },
    {
      question: 'What integrations are supported?',
      answer: 'We maintain native connectors for NetSuite, ERPNext, Odoo, Workday, Snowflake, and offer a REST & event-based API for custom workflows.'
    }
  ]
}
